import * as React from "react";
import { Combobox } from "@base-ui/react/combobox";
import { useVirtualizer } from "@tanstack/react-virtual";
import ComboboxItem from "./ComboboxItem";
import {
  ComboboxGroupHeaderItem,
  ComboboxGroupHeaderCheckbox,
  ComboboxSelectAllItem,
} from "./ComboboxStyles";
import type { DataWithId } from "./types";
import {
  isPlaceholderSentinel,
  isCreatableSentinel,
  type CreatableSentinel,
} from "./sentinels";

export const ITEM_HEIGHT = 32;

interface GroupHeaderSentinel {
  __groupHeader: true;
  groupName: string;
}

interface SelectAllSentinel {
  __selectAll: true;
}

export function isGroupHeaderSentinel(v: unknown): v is GroupHeaderSentinel {
  return typeof v === "object" && v !== null && "__groupHeader" in v;
}

export function isSelectAllSentinel(v: unknown): v is SelectAllSentinel {
  return typeof v === "object" && v !== null && "__selectAll" in v;
}

export type FlatSentinelRow<T> =
  | T
  | GroupHeaderSentinel
  | SelectAllSentinel
  | CreatableSentinel;

interface VirtualizedComboboxListProps<T extends DataWithId> {
  open: boolean;
  virtualizerRef: React.RefObject<ReturnType<
    typeof useVirtualizer<HTMLDivElement, Element>
  > | null>;
  value: T | T[] | null;
  groups: Array<{ value: string; items: T[] }> | null;
  // `data` contains only real items (no sentinels). Sentinel rows (placeholder,
  // group headers, select-all) come exclusively from `filteredItems` via
  // Combobox.useFilteredItems. This means `data.length` is the correct count
  // of selectable items for select-all logic.
  data: T[];
  itemToString: (item: T) => string;
  inputType: "icon" | "radio" | "checkbox" | "none";
  renderItem?: (item: T) => React.ReactNode;
  renderGroupHeading?: (label: string) => React.ReactNode;
  isGroupHeaderDisabled?: (groupName: string) => boolean;
  renderCreatableItem?: (
    sentinel: CreatableSentinel,
    style: React.CSSProperties,
    index: number,
    measureRef: (el: Element | null) => void
  ) => React.ReactNode;
}

export default function VirtualizedComboboxList<T extends DataWithId>({
  open,
  virtualizerRef,
  value,
  groups,
  data,
  itemToString,
  inputType,
  renderItem,
  renderGroupHeading,
  isGroupHeaderDisabled,
  renderCreatableItem,
}: VirtualizedComboboxListProps<T>) {
  type Row = FlatSentinelRow<T>;

  const filteredItems = Combobox.useFilteredItems<Row>();
  const scrollElementRef = React.useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    enabled: open,
    count: filteredItems.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 20,
    paddingStart: 4,
    paddingEnd: 4,
    scrollPaddingStart: 4,
    scrollPaddingEnd: 4,
  });

  React.useImperativeHandle(virtualizerRef, () => virtualizer);

  const handleScrollRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      scrollElementRef.current = el;
      if (el) requestAnimationFrame(() => virtualizer.measure());
    },
    [virtualizer]
  );

  if (!filteredItems.length) return null;

  const selectedArray = Array.isArray(value)
    ? value
    : value != null
    ? [value]
    : [];

  const totalSize = virtualizer.getTotalSize();

  return (
    <div
      role="presentation"
      ref={handleScrollRef}
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overscrollBehavior: "contain",
      }}
    >
      <div
        role="presentation"
        style={{ position: "relative", width: "100%", height: totalSize }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const row = filteredItems[virtualItem.index];
          if (row == null) return null;

          const baseStyle: React.CSSProperties = {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${virtualItem.start}px)`,
            boxSizing: "border-box",
          };

          if (isPlaceholderSentinel(row)) {
            return (
              <ComboboxItem
                key="placeholder"
                index={virtualItem.index}
                data-index={virtualItem.index}
                value={row as unknown as T}
                itemId="placeholder"
                label={row.label}
                inputType={inputType}
                style={baseStyle}
                ref={virtualizer.measureElement}
              />
            );
          }

          if (isSelectAllSentinel(row)) {
            const selectedCount = selectedArray.length;
            const totalCount = data.length;
            const state =
              selectedCount === 0
                ? "none"
                : selectedCount === totalCount
                ? "all"
                : "partial";
            return (
              <ComboboxSelectAllItem
                key={virtualItem.key}
                index={virtualItem.index}
                data-index={virtualItem.index}
                value={row}
                style={baseStyle}
                ref={virtualizer.measureElement}
              >
                <ComboboxGroupHeaderCheckbox $state={state} id="__selectAll" />
                Select all
              </ComboboxSelectAllItem>
            );
          }

          if (isGroupHeaderSentinel(row)) {
            const group = groups?.find((g) => g.value === row.groupName);
            const groupItems = group?.items ?? [];
            const selectedCount = groupItems.filter((item) =>
              selectedArray.some((s) => s.id === item.id)
            ).length;
            const state =
              selectedCount === 0
                ? "none"
                : selectedCount === groupItems.length
                ? "all"
                : "partial";
            return (
              <ComboboxGroupHeaderItem
                key={virtualItem.key}
                index={virtualItem.index}
                data-index={virtualItem.index}
                value={row}
                disabled={isGroupHeaderDisabled?.(row.groupName)}
                style={baseStyle}
                ref={virtualizer.measureElement}
              >
                <ComboboxGroupHeaderCheckbox
                  $state={state}
                  id={`__header-${row.groupName}`}
                />
                {renderGroupHeading
                  ? renderGroupHeading(row.groupName)
                  : row.groupName}
              </ComboboxGroupHeaderItem>
            );
          }

          if (isCreatableSentinel(row)) {
            if (renderCreatableItem) {
              return renderCreatableItem(
                row,
                baseStyle,
                virtualItem.index,
                virtualizer.measureElement
              );
            }
            return null;
          }

          const item = row as T;
          return (
            <ComboboxItem
              key={virtualItem.key}
              index={virtualItem.index}
              data-index={virtualItem.index}
              value={item}
              itemId={String(item.id)}
              label={itemToString(item)}
              disabled={item.disabled}
              inputType={inputType}
              renderItem={
                renderItem ? () => renderItem(item) : () => itemToString(item)
              }
              style={baseStyle}
              ref={virtualizer.measureElement}
            />
          );
        })}
      </div>
    </div>
  );
}
